/**
 * frontend/src/components/valuation/StepFooter.tsx
 *
 * VAL-PATH-FOOTER (P2a) — the one footer every valuation step ends with.
 * Back and "Save and continue" (and a step-bar click, through the mover) all
 * save first, then move (U2): `save` reports whether anything was sent and
 * when the step was last saved; a failure keeps the person on the step with
 * the message inline (unless the screen threw StepBlocked: it shows its own).
 * Targets come from the one step list (U4). The last step
 * shows the action the screen supplies (`final`) instead of a Next.
 * `beforeNext` runs after the save, on "Save and continue" only (Intake runs
 * the valuation there); its failure keeps the person on the step too.
 *
 * The save result rides to the next step in router state, where its footer
 * shows "Saved at <time>" or "No changes — last saved at <time>", in Central
 * time (WALKFIX-C F2: server stamps are UTC).
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { StepBlocked, useStepContext, useStepMover, type StepSaveResult } from '@/pages/valuation/_stepContext'
import { BACK, SAVE_AND_CONTINUE, stepBack, stepNext, type PathStepKey } from '@/pages/valuation/_steps'
import { formatTimeCentral } from '@/utils/date'
import { formatApiError } from '@/utils/httpErrors'

export interface StepFinalAction {
  label: string
  onClick: () => void | Promise<void>
  disabled?: boolean
}

export interface StepFooterProps {
  step: PathStepKey
  engagementId: string
  role?: string
  /** The step's save; omitted on a step with nothing to save. */
  save?: () => Promise<StepSaveResult>
  /** The last step's action ("Record and order my Snapshot", ...). */
  final?: StepFinalAction
  /** Runs after a good save, before moving forward (not on Back). */
  beforeNext?: () => Promise<void>
  nextDisabled?: boolean
  /** A short line beside the buttons (why Next is disabled, say). */
  note?: string | null
}

function savedLine(saved: StepSaveResult): string | null {
  const at = formatTimeCentral(saved.savedAt)
  if (!at) return null
  return saved.changed ? `Saved at ${at}` : `No changes — last saved at ${at}`
}

export function StepFooter({ step, engagementId, role, save, final, beforeNext, nextDisabled = false, note }: StepFooterProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const ctx = useStepContext()
  const mover = useStepMover()
  const who = role ?? ctx?.role
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const back = stepBack(step, who, engagementId) ?? '/valuation'
  const next = stepNext(step, who, engagementId)

  const saveFirst = async (): Promise<StepSaveResult | null> => {
    setError(null)
    setBusy(true)
    try {
      const out = save ? await save() : { changed: false, savedAt: null }
      return { changed: out.changed, savedAt: out.savedAt ?? ctx?.engagement?.updated_at ?? null }
    } catch (e) {
      if (!(e instanceof StepBlocked)) setError(formatApiError(e))
      return null
    } finally {
      setBusy(false)
    }
  }

  const moveTo = async (to: string) => {
    const saved = await saveFirst()
    if (saved) navigate(to, { state: { saved } })
  }

  const moveNext = async (to: string) => {
    if (!beforeNext) return moveTo(to)
    const saved = await saveFirst()
    if (!saved) return
    setBusy(true)
    try {
      await beforeNext()
    } catch (e) {
      if (!(e instanceof StepBlocked)) setError(formatApiError(e))
      return
    } finally {
      setBusy(false)
    }
    navigate(to, { state: { saved } })
  }

  const runFinal = async () => {
    if (!final) return
    if (await saveFirst()) await final.onClick()
  }

  // The step bar moves through this footer while it is on screen.
  const latest = useRef(moveTo)
  latest.current = moveTo
  useEffect(() => {
    if (!mover) return
    const move = (to: string) => latest.current(to)
    mover.current = move
    return () => { if (mover.current === move) mover.current = null }
  }, [mover])

  const arrived = (location.state as { saved?: StepSaveResult } | null)?.saved
  const chip = arrived ? savedLine(arrived) : null

  return (
    <div data-testid="step-footer" className="mt-8 space-y-3 border-t border-gray-200 pt-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="outline" disabled={busy} onClick={() => void moveTo(back)}>
          {BACK}
        </Button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {chip && <span role="status" className="text-sm text-gray-500">{chip}</span>}
          {note && <span className="text-sm text-gray-500">{note}</span>}
          {final ? (
            <Button type="button" disabled={busy || !!final.disabled} onClick={() => void runFinal()}>
              {final.label}
            </Button>
          ) : next && (
            <Button type="button" disabled={busy || nextDisabled} onClick={() => void moveNext(next)}>
              {SAVE_AND_CONTINUE}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
